import React from 'react';
import ContentLoader from 'react-content-loader';
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton';
import { uniqueId } from 'lodash';
import { useTokenStyles } from 'pages/AssetsPage/components/Stx20Tab/components/Tabs/TokenTab/TokenStyles';

export const SkeletonAsset = ({ mode }: { mode: number }) => {
  return (
    <SkeletonTheme baseColor="#383A3F" highlightColor="#51545B">
      <div style={{ padding: '0 32px' }}>
        {[1, 2, 3, 4].map((_, index) => (
          <div
            className="grid items-center justify-between grid-cols-[22%_22%_22%_22%_12%] border-b border-[#EEEDF2] py-16"
            key={uniqueId(index.toString())}
          >
            <div className="flex">
              <Skeleton width={36} height={36} circle />
              <div className="flex flex-col w-2/4 ml-16">
                <Skeleton height={20} />
                <Skeleton height={10} />
              </div>
            </div>
            <div className="flex flex-col w-2/4">
              <Skeleton height={20} />
              <Skeleton height={10} />
            </div>
            <div className="flex flex-col w-2/4">
              <Skeleton height={20} />
              <Skeleton height={10} />
            </div>
            <div className="flex flex-col w-2/4">
              <Skeleton height={20} />
              <Skeleton height={10} />
            </div>
            <div className="flex flex-col w-2/4 self-start">
              <Skeleton height={27} />
            </div>
          </div>
        ))}
      </div>
    </SkeletonTheme>
  );
};

export const SkeletonSafe = () => {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-16 lg:gap-24 xl:gap-32 flex-wrap">
      {[1, 2, 3, 4].map((item) => (
        <ContentLoader
          key={uniqueId(`sk${item}`)}
          speed={2}
          height="100%"
          width="100%"
          backgroundColor="#FFFFFF1A"
          foregroundColor="#43464D"
          className="px-24 py-20 rounded-[12px] w-full bg-[#1F2023]"
        >
          <circle cx="22" cy="22" r="22" />
          <rect x="57" y="6" rx="6" ry="4" width="100" height="16" />
          <rect x="57" y="28" rx="6" ry="4" width="80" height="12" />
          <rect x="0" y="67" rx="0" ry="0" width="100%" height="1" />
          <rect x="135" y="96" rx="6" ry="4" width="95" height="20" />
          <rect x="135" y="125" rx="6" ry="4" width="95" height="20" />
          <circle cx="17" cy="105" r="13" />
          <circle cx="32" cy="105" r="13" />
          <circle cx="47" cy="105" r="13" />
          <rect x="0" y="125" rx="6" ry="4" width="65" height="20" />
        </ContentLoader>
      ))}
    </div>
  );
};

export const SkeletonContact = () => {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-16 lg:gap-24 xl:gap-32 flex-wrap">
      {[1, 2, 3, 4].map((item) => (
        <ContentLoader
          key={uniqueId(`sk${item}`)}
          speed={2}
          height="100%"
          width="100%"
          backgroundColor="#FFFFFF1A"
          foregroundColor="#43464D"
          className="px-24 py-20 rounded-[12px] w-full bg-[#1F2023]"
        >
          <circle cx="32" cy="32" r="32" />
          <rect x="77" y="6" rx="6" ry="4" width="160" height="24" />
          <rect x="77" y="36" rx="6" ry="4" width="80" height="22" />
          <rect x="167" y="36" rx="6" ry="4" width="22" height="22" />
          <rect x="197" y="36" rx="6" ry="4" width="22" height="22" />
        </ContentLoader>
      ))}
    </div>
  );
};

export const SkeletonGetTxFee = () => {
  return (
    <SkeletonTheme baseColor="#383A3F" highlightColor="#51545B">
      <div className="grid gap-16">
        {[1, 2, 3].map((item, index) => (
          <div
            key={uniqueId(index.toString())}
            className="flex justify-between items-center px-16 py-12 border border-[#292A2F] bg-[#292A2F] h-68 rounded-[8px]"
          >
            <div className="grid gap-4">
              <Skeleton height={22} width={80} borderRadius={4} />
              <Skeleton height={11} width={48} borderRadius={4} />
            </div>
            <div className="ml-auto">
              <Skeleton height={22} width={140} borderRadius={4} />
            </div>
          </div>
        ))}
      </div>
    </SkeletonTheme>
  );
};

export const SkeletonNftCollection = () => {
  return (
    <div className="grid md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 flex-wrap gap-24 2xl:gap-32">
      {[1, 2, 3, 4].map((item) => (
        <ContentLoader
          key={uniqueId(`nft${item}`)}
          speed={2}
          height="100%"
          width="100%"
          backgroundColor="#FFFFFF1A"
          foregroundColor="#43464D"
          className="px-24 py-20 rounded-[12px] w-full bg-[#1F2023]"
        >
          <rect x="20%" y="6" rx="6" ry="4" width="60%" height="80" />
          <rect x="30%" y="90" rx="6" ry="4" width="40%" height="21" />
          <rect x="0" y="115" rx="6" ry="4" width="100%" height="32" />
        </ContentLoader>
      ))}
    </div>
  );
};

export const SkeletonNftItem = () => {
  return (
    <SkeletonTheme baseColor="#383A3F" highlightColor="#51545B">
      {Array(20)
        .fill('')
        .map((item) => (
          <div className="grid gap-12 justify-items-center" key={uniqueId()}>
            <Skeleton className="w-[177px] h-[204px] rounded-[12px] border border-[#292A2F] bg-gradient-to-r from-[#232427] to-[#151618] p-6 justify-items-center content-start sm:w-[204px]" />
            <Skeleton width={126} height={24} borderRadius={4} />
          </div>
        ))}
    </SkeletonTheme>
  );
};

export const SkeletonNftTitle = () => {
  return (
    <SkeletonTheme baseColor="#383A3F" highlightColor="#51545B">
      <Skeleton width={126} height={24} borderRadius={4} />
    </SkeletonTheme>
  );
};

export const SkeletonNftAddress = () => {
  return (
    <SkeletonTheme baseColor="#383A3F" highlightColor="#51545B">
      <Skeleton width={426} height={24} borderRadius={4} />
    </SkeletonTheme>
  );
};

export const SkeletonStx20 = () => {
  const classes = useTokenStyles();

  return (
    <SkeletonTheme baseColor="#383A3F" highlightColor="#51545B">
      <div className={classes.contentWrapper}>
        {[1, 2, 3].map((item) => (
          <div className={classes.tableRow} key={uniqueId(`stx20-${item}`)}>
            <div className={classes.token}>
              <div className={classes.rowTableIcon}>
                <Skeleton height={36} width={36} borderRadius={36} />
              </div>
              <div className={classes.title}>
                <Skeleton height={20} width={50} borderRadius={4} />
              </div>
            </div>
            <div className={classes.itemRow}>
              <div className={classes.title}>
                <Skeleton height={20} width={50} borderRadius={4} />
              </div>
            </div>
            <div className={classes.itemColumn}>
              <div className={classes.title}>
                <Skeleton height={20} width={50} borderRadius={4} />
              </div>
            </div>
            <div className={classes.itemRow}></div>
          </div>
        ))}
      </div>
    </SkeletonTheme>
  );
};
